import {Component, h, Host, Prop, State} from '@stencil/core';
import {CompanyRegistrationViewModel} from "@modules/onBoarding/presentation/viewModel/CompanyRegistrationViewModel";
import {
    ValidateCompanyRegistrationInfo
} from "@modules/onBoarding/application/usecase/ValidateCompanyRegistrationInfo/ValidateCompanyRegistrationInfo";
import {
    CompanyRegistrationFormInformation
} from "@modules/onBoarding/application/port/output/CompanyRegistrationFormValidator";
import clsx from "clsx";

type CompanyRegistrationState = {
    companyName: string;
    vatNumber: string;
    zipCode: string;
    city: string;
    street: string;
};

@Component({
    tag: 'company-registration-view',
    styleUrl: 'company-registration-view.css',
    scoped: true,
})
export class CompanyRegistrationView {

    @Prop() viewModel: CompanyRegistrationViewModel;

    private validateCompanyRegistrationInfoUseCase: ValidateCompanyRegistrationInfo;

    @State() companyRegistrationState: CompanyRegistrationState = {
        companyName: '',
        vatNumber: '',
        zipCode: '',
        city: '',
        street: ''
    };
    @State() errors: { [key: string]: string } = {};

    async componentWillLoad() {
        const module = await import('@modules/onBoarding');
        this.validateCompanyRegistrationInfoUseCase = module.validateCompanyRegistrationInfo;
    }

    private readonly handleFormChange = async (e: Event) => {
        const target = e.target as HTMLInputElement;
        const updatedKey = target.id;
        let updatedValue = target.value;

        this.updateField(updatedKey, updatedValue);
        await this.updateErrors(updatedKey);
    }

    private updateField(key: string, value: string | boolean) {
        this.companyRegistrationState = {
            ...this.companyRegistrationState,
            [key]: value
        };
    }

    private async updateErrors(updatedKey: string) {
        const result = await this.validateCompanyRegistrationInfoUseCase.execute({
            field: updatedKey as CompanyRegistrationFormInformation,
            value: this.companyRegistrationState[updatedKey]
        });

        this.errors[updatedKey] = null;
        if (!result.success) {
            this.errors = {...this.errors, [updatedKey]: this.viewModel.errorMessages[updatedKey + "ErrorMessages"][result.errorKey]};
        }
    }

    getInputClasses(element: string) {
        return clsx(
            "form-control",
            {
                "is-invalid": !!this.errors[element],
            }
        )
    }

    render() {
        return (
            <Host>
                <div id="register-company-view" class="d-flex justify-content-center bg-primary position-relative">
                    <div class="container position-absolute">
                        <div id="content-container" class="mx-auto">
                            <svg class="my-3" aria-hidden="true" width="100%" height="2rem">
                                <use
                                    data-href={this.viewModel.taglineHref}></use>
                            </svg>
                            <div class="card shadow container pt-4 pb-5">
                                <div class="ml-4">
                                    <a href={this.viewModel.modifySearchLink}>{this.viewModel.modifySearch}</a>
                                </div>
                                <form class="col-12 col-sm-8 offset-sm-2" data-testid="onboarding.register-company-view.form">
                                    <nav class="my-4">
                                    </nav>
                                    <span
                                        class="title_xxs text-primary font-weight-bold">{this.viewModel.companyNotOnFile}</span>
                                    <hr class="bg-lyreco-green mb-4"/>
                                    <h1 class="title_sm mb-4">{this.viewModel.addCompanyInformation}</h1>

                                    <div class="d-flex">
                                        <div class="form-group w-50 mb-4">
                                            <label class="text-primary font-weight-bold"
                                                   htmlFor="companyName">{this.viewModel.companyNameLabel}</label>
                                            <div class="mb-1">
                                                <input type="text" class={this.getInputClasses("companyName")} name="companyName"
                                                       id="companyName"
                                                       placeholder={this.viewModel.companyNamePlaceholder}
                                                       data-testid="onboarding.company-registration.form.companyName"
                                                       onBlur={this.handleFormChange}
                                                />
                                                {
                                                    this.errors.companyName && (
                                                        <p data-testid="onboarding.company-registration.form.companyName.error"
                                                           class="invalid-feedback d-block">
                                                            {this.errors.companyName}
                                                        </p>
                                                    )
                                                }
                                            </div>
                                        </div>
                                        <div class="pl-3 form-group w-50 mb-4">
                                            <label class="text-primary font-weight-bold"
                                                   htmlFor="vatNumber">{this.viewModel.vatNumberLabel}</label>

                                            <div>
                                                <input type="text" class={this.getInputClasses("vatNumber")} name="vatNumber"
                                                       id="vatNumber"
                                                       placeholder={this.viewModel.vatNumberPlaceholder}
                                                       data-testid="onboarding.company-registration.form.vatNumber"
                                                       onBlur={this.handleFormChange}
                                                />
                                                {
                                                    this.errors.vatNumber && (
                                                        <p data-testid="onboarding.company-registration.form.vatNumber.error"
                                                           class="invalid-feedback d-block">
                                                            {this.errors.vatNumber}
                                                        </p>
                                                    )
                                                }
                                            </div>
                                        </div>
                                    </div>
                                    <div class="d-flex">
                                        <div class="form-group w-50 mb-4 zip-code-group">
                                            <label class="text-primary font-weight-bold"
                                                   htmlFor="zipCode">{this.viewModel.zipCodeLabel}</label>
                                            <div class="mb-1">
                                                <input type="text" class={this.getInputClasses("zipCode")} name="zipCode"
                                                       id="zipCode"
                                                       placeholder={this.viewModel.zipCodePlaceholder}
                                                       data-testid="onboarding.company-registration.form.zipCode"
                                                       onBlur={this.handleFormChange}
                                                />
                                                {
                                                    this.errors.zipCode && (
                                                        <p data-testid="onboarding.company-registration.form.zipCode.error"
                                                           class="invalid-feedback d-block">
                                                            {this.errors.zipCode}
                                                        </p>
                                                    )
                                                }
                                            </div>
                                        </div>
                                        <div class="pl-3 form-group w-50 mb-4 w-100">
                                            <label class="text-primary font-weight-bold"
                                                   htmlFor="city">{this.viewModel.cityLabel}</label>

                                            <div>
                                                <input type="text" class={this.getInputClasses("city")} name="city"
                                                       id="city"
                                                       placeholder={this.viewModel.cityPlaceholder}
                                                       data-testid="onboarding.company-registration.form.city"
                                                       onBlur={this.handleFormChange}
                                                />
                                                {
                                                    this.errors.city && (
                                                        <p data-testid="onboarding.company-registration.form.city.error"
                                                           class="invalid-feedback d-block">
                                                            {this.errors.city}
                                                        </p>
                                                    )
                                                }
                                            </div>
                                        </div>
                                    </div>
                                    <div class="form-group">
                                        <label class="text-primary font-weight-bold"
                                               htmlFor="street">{this.viewModel.streetLabel}</label>

                                        <div>
                                            <input type="text" class={this.getInputClasses("street")} name="street"
                                                   id="street"
                                                   placeholder={this.viewModel.streetPlaceholder}
                                                   data-testid="onboarding.company-registration.form.street"
                                                   onBlur={this.handleFormChange}
                                            />
                                            {
                                                this.errors.street && (
                                                    <p data-testid="onboarding.company-registration.form.street.error"
                                                       class="invalid-feedback d-block">
                                                        {this.errors.street}
                                                    </p>
                                                )
                                            }
                                        </div>
                                    </div>
                                    <span class="small text_secondary">{this.viewModel.mandatoryFields}</span>
                                    <hr class="mb-4"/>
                                    <button
                                        class="btn btn-outline-primary w-100">{this.viewModel.confirmCompanyDetails}</button>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
            </Host>
        );
    }
}
